import { Search, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../../constants/constants";

// Définition du type pour les formations
interface Formateur {
  id: number;
  nom: string;
  image: string;
}

interface Formation {
  id: number;
  titre: string;
  image: string;
  bgClass: string;
  formateur: Formateur;
  description: string;
}

interface Module {
  id: number;
  titre: string;
  statut: "terminé" | "en cours" | "non commencé";
  pourcentage: number;
  dateDebut: string;
  dateDernierAcces: string;
}

// Types pour les données de l'API
interface ApiModule {
  id: string;
  title: string;
  description: string;
  order: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
}

const EtudiantDashboard = () => {
  // États pour la gestion des données depuis l'API
  const [formations, setFormations] = useState<Formation[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");

  // Mapping des formations avec images et formateurs
  const formationMapping: Record<string, Formation> = {
    "1": {
      id: 1,
      titre: "Fondements et valeurs de la République",
      image: "/images/valeur.png",
      bgClass: "bg-gradient-to-r from-pink-100 to-purple-100",
      formateur: {
        id: 1,
        nom: "Stanislas ZEZE",
        image: "/images/Photo-Zeze-Stanislas.jpg",
      },
      description:
        "Approchez les fondements et valeurs essentielles de la République pour une meilleure compréhension du fonctionnement publique.",
    },
    "2": {
      id: 2,
      titre: "Éthique et déontologie du Fonctionnaire et Agent de l'État",
      image: "/images/Ethique-et-Deontologie.jpg",
      bgClass: "bg-gray-300",
      formateur: {
        id: 2,
        nom: "Gilles ATAYI",
        image: "/images/Gilles_Attayi.jpg",
      },
      description:
        "Comprendre les principes éthiques et déontologiques régissant les fontionnaires et agents de l'État.",
    },
    "3": {
      id: 3,
      titre: "Corruption et bonne gouvernance",
      image: "/images/Corruption.png",
      bgClass: "bg-blue-100",
      formateur: {
        id: 3,
        nom: "Simplice Dion YODÉ",
        image: "/images/siimplice.jpg",
      },
      description:
        "Maîtriser les concepts de corruption et de bonne gouvernance pour une administration transparente et efficace.",
    },
    "4": {
      id: 4,
      titre: "Leadership et Administration publique",
      image: "/images/leadership.webp",
      bgClass: "bg-gray-300",
      formateur: {
        id: 4,
        nom: "Odette KOUASSI",
        image: "/images/marie-kone.jpg",
      },
      description:
        "Développer les compétences de leadership nécessaires pour une administration publique performante.",
    },
  };

  // Charger les données au montage du composant
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("accessToken");
        
        if (!accessToken) {
          throw new Error("Vous n'êtes pas connecté");
        }

        // Récupérer les informations de l'utilisateur du localStorage
        const userInfo = localStorage.getItem("user");
        if (userInfo) {
          const userData = JSON.parse(userInfo);
          setUserData(userData);
        }

        // 🔄 ÉTAPE 1: Recalculer toutes les progressions
        console.log("📊 Recalcul des progressions...");
        try {
          const recalculateResponse = await fetch(
            API_ENDPOINTS.CINEPRO.RECALCULATE_PROGRESSION,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (recalculateResponse.ok) {
            const recalculateData = await recalculateResponse.json();
            console.log("✅ Progressions recalculées:", recalculateData);
          } else {
            console.warn("⚠️ Impossible de recalculer les progressions, on continue...");
          }
        } catch (err) {
          console.warn("⚠️ Erreur lors du recalcul des progressions:", err);
          // On continue même si le recalcul échoue
        }

        // 📦 ÉTAPE 2: Récupérer tous les modules depuis l'API
        console.log("📚 Chargement des modules...");
        const modulesResponse = await fetch(
          API_ENDPOINTS.CINEPRO.GET_MODULES,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!modulesResponse.ok) {
          throw new Error("Impossible de charger les modules");
        }

        const modulesData = await modulesResponse.json();
        console.log("📦 Données brutes des modules:", modulesData);

        // Gérer différents formats de réponse de l'API
        let apiModules: ApiModule[] = [];
        
        if (Array.isArray(modulesData)) {
          // Format 1: Array simple
          apiModules = modulesData;
        } else if (modulesData && typeof modulesData === "object") {
          // Format 2: Objet avec activeModule, otherModules, etc.
          if (modulesData.activeModule) {
            apiModules.push(modulesData.activeModule);
          }
          if (Array.isArray(modulesData.otherModules)) {
            apiModules = [...apiModules, ...modulesData.otherModules];
          }
          if (Array.isArray(modulesData.modules)) {
            apiModules = modulesData.modules;
          }
        }

        console.log("✅ Modules traités:", apiModules);

        if (apiModules.length === 0) {
          console.warn("⚠️ Aucun module trouvé");
        }

        // 📈 ÉTAPE 3: Récupérer les modules accessibles et complétés
        console.log("📊 Chargement des données d'accessibilité...");
        
        let accessibleModuleIds: string[] = [];
        let completedModuleIds: string[] = [];
        
        try {
          const accessibilityResponse = await fetch(
            API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (accessibilityResponse.ok) {
            const accessibilityData = await accessibilityResponse.json();
            console.log("✅ DONNÉES BRUTES DE GET_PROGRESSION:", accessibilityData);
            console.log("   Type:", typeof accessibilityData);
            console.log("   Est un array?", Array.isArray(accessibilityData));
            console.log("   Clés de l'objet:", Object.keys(accessibilityData || {}));

            // Extraire les IDs de modules accessibles et les normaliser en nombres
            if (accessibilityData.accessibleModules && Array.isArray(accessibilityData.accessibleModules)) {
              accessibleModuleIds = accessibilityData.accessibleModules.map((id: any) => String(id));
              console.log("✅ accessibleModules trouvé:", accessibleModuleIds);
            } else {
              console.warn("❌ accessibleModules non trouvé ou pas un array");
            }
            
            if (accessibilityData.completedModules && Array.isArray(accessibilityData.completedModules)) {
              completedModuleIds = accessibilityData.completedModules.map((id: any) => String(id));
              console.log("✅ completedModules trouvé:", completedModuleIds);
            } else {
              console.warn("❌ completedModules non trouvé ou pas un array");
            }
          } else {
            throw new Error(`GET_PROGRESSION retourné ${accessibilityResponse.status}: ${accessibilityResponse.statusText}`);
          }
        } catch (err) {
          console.error("❌ ERREUR lors de la récupération d'accessibilité:", err);
          throw err; // En production, on lance l'erreur au lieu de continuer avec du vide
        }

        console.log(`✅ Modules accessibles: ${accessibleModuleIds.length}`, accessibleModuleIds);
        console.log(`✅ Modules complétés: ${completedModuleIds.length}`, completedModuleIds);
        console.log("🔍 Types d'IDs - accessible[0]:", typeof accessibleModuleIds[0], "valeur:", accessibleModuleIds[0]);
        console.log("🔍 Types d'IDs - completed[0]:", typeof completedModuleIds[0], "valeur:", completedModuleIds[0]);
        
        // Fonction helper pour déterminer le statut BASÉ SUR LA PROGRESSION
        const getStatut = (moduleId: string, progression: number): "terminé" | "en cours" | "non commencé" => {
          // Priorité: basée sur la progression calculée
          if (progression === 100) return "terminé";
          if (progression > 0) return "en cours";
          // Sinon, basée sur les arrays
          if (completedModuleIds.includes(moduleId)) return "terminé";
          if (accessibleModuleIds.includes(moduleId)) return "en cours";
          return "non commencé";
        };
        
        // 📈 ÉTAPE 4: Récupérer la progression pour chaque module accessible (en parallèle)
        console.log("📊 Chargement des progressions des modules...");
        
        // Traiter UNIQUEMENT les modules accessibles ou complétés (PAS DE FALLBACK)
        const modulesToProcess = apiModules
          .filter((m) => {
            const isAccessible = accessibleModuleIds.includes(String(m.id));
            const isCompleted = completedModuleIds.includes(String(m.id));
            console.log(`🔍 Module ${m.id}: accessible=${isAccessible}, completed=${isCompleted}`);
            return isAccessible || isCompleted;
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        
        console.log(`📂 Modules à traiter: ${modulesToProcess.length}/${apiModules.length} (DONNÉES RÉELLES SEULEMENT)`, modulesToProcess);
        
        if (modulesToProcess.length === 0) {
          console.warn("⚠️ AUCUN MODULE ACCESSIBLE OU COMPLET - Affichage de rien");
        }
        
        // Créer les promises pour tous les modules en parallèle
        const modulePromises = modulesToProcess.map(async (apiModule) => {
          const moduleId = String(apiModule.id);
          const module: Module = {
            id: Number(apiModule.id),
            titre: apiModule.title,
            statut: getStatut(moduleId, 0), // Initialiser avec progression 0
            pourcentage: 0,
            dateDebut: new Date(apiModule.createdAt).toLocaleDateString("fr-FR"),
            dateDernierAcces: new Date(apiModule.updatedAt).toLocaleDateString("fr-FR"),
          };

          try {
            // Récupérer la progression spécifique du module
            const moduleProgResponse = await fetch(
              API_ENDPOINTS.CINEPRO.GET_MODULE_PROGRESSION_COURSES(apiModule.id),
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (moduleProgResponse.ok) {
              let moduleProgData = await moduleProgResponse.json();
              console.log(`✅ Progression du module ${apiModule.id}:`, moduleProgData);

              // Gérer différents formats de réponse
              let coursesData: any[] = [];
              if (Array.isArray(moduleProgData)) {
                coursesData = moduleProgData;
              } else if (moduleProgData && typeof moduleProgData === "object") {
                if (Array.isArray(moduleProgData.courses)) {
                  coursesData = moduleProgData.courses;
                } else if (moduleProgData.data && Array.isArray(moduleProgData.data)) {
                  coursesData = moduleProgData.data;
                }
              }

              // Calculer la progression moyenne si nous avons des données de cours
              if (coursesData.length > 0) {
                const progressions = coursesData.map((p: any) => 
                  Math.min(100, Math.max(0, Number(p.progression) || Number(p.completed ? 100 : 0)))
                );
                module.pourcentage = Math.round(
                  progressions.reduce((a, b) => a + b, 0) / progressions.length
                );
                module.statut = getStatut(moduleId, module.pourcentage); // Mettre à jour le statut avec la progression calculée
                
                // Récupérer la date du dernier accès
                const dates = coursesData
                  .map((p: any) => p.lastWatchedAt || p.updatedAt || p.lastAccessed || apiModule.updatedAt)
                  .filter((d: string) => d)
                  .map((d: string) => new Date(d).getTime());
                
                if (dates.length > 0) {
                  const lastAccess = Math.max(...dates);
                  module.dateDernierAcces = new Date(lastAccess).toLocaleDateString("fr-FR");
                }
              }
            } else {
              console.warn(`⚠️ Impossible de récupérer la progression du module ${apiModule.id}: ${moduleProgResponse.status}`);
            }

            return module;
          } catch (err) {
            console.error(`❌ Erreur pour le module ${apiModule.id}:`, err);
            // Retourner le module avec progression 0 en cas d'erreur
            return module;
          }
        });

        // Attendre toutes les promesses en parallèle
        const transformedModules = await Promise.all(modulePromises);

        console.log("🎉 Modules transformés finaux:", transformedModules);
        console.log(`📊 ${transformedModules.length} modules sont prêts à être affichés`);

        setModules(transformedModules);

        // Créer les formations basées sur les modules
        const formationsFromModules: Formation[] = transformedModules.map(
          (module) => {
            const formationData = formationMapping[String(module.id)];
            return (
              formationData || {
                id: module.id,
                titre: module.titre,
                image: "/images/valeur.png",
                bgClass: "bg-gradient-to-r from-pink-100 to-purple-100",
                formateur: {
                  id: module.id,
                  nom: "Formateur",
                  image: "/images/avatar-placeholder.png",
                },
                description: module.titre,
              }
            );
          }
        );

        setFormations(formationsFromModules);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Erreur inconnue";
        setError(errorMessage);
        console.error("Erreur lors du chargement du dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  return (
    <div className="bg-[#fefaf6]">
      <div className="container mx-auto px-4 py-4 mobile:px-3 tablet:px-4">
        {/* État de chargement ou d'erreur */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-600">Erreur: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* En-tête de bienvenue */}
            <div className="mb-6 mobile:mb-4 tablet:mb-5">
              <h1 className="text-2xl font-bold text-gray-800 mobile:text-xl tablet:text-xl desktop:text-3xl">
                Bienvenue {userData?.firstName} {userData?.lastName}
              </h1>
              <p className="text-sm text-gray-600 mobile:text-xs tablet:text-sm desktop:text-base">
                Développez vos compétences professionnelles à votre rythme
              </p>
            </div>

            {/* Cartes de progression */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mobile:gap-3 tablet:gap-4 mobile:mb-6 tablet:mb-6">
              {/* Carte d'apprentissage global */}
              <div className="border border-green-300 rounded-md p-4 mobile:p-3 tablet:p-3 bg-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold mobile:text-sm tablet:text-base">
                      Votre apprentissage global
                    </h2>
                    <p className="text-xs text-gray-500 mobile:text-[10px] tablet:text-xs">
                      Dernier accès le{" "}
                      {modules.length > 0
                        ? modules.reduce((latest, m) => {
                            const latestDate = new Date(latest.dateDernierAcces);
                            const currentDate = new Date(m.dateDernierAcces);
                            return currentDate > latestDate ? m : latest;
                          }).dateDernierAcces
                        : ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mobile:text-[10px] tablet:text-xs">
                      {modules.filter((m) => m.statut === "terminé").length} sur{" "}
                      {modules.length} modules
                    </p>
                  </div>
                  <div
                    className={`${
                      modules.length > 0
                        ? "bg-orange-50 text-orange-500"
                        : "bg-gray-50 text-gray-500"
                    } rounded-full p-2 flex items-center justify-center h-10 w-10 mobile:h-8 mobile:w-8 tablet:h-9 tablet:w-9`}
                  >
                    <span className="font-bold mobile:text-sm tablet:text-base">
                      {modules.length > 0
                        ? Math.round(
                            (modules.filter((m) => m.statut === "terminé")
                              .length /
                              modules.length) *
                              100
                          )
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-8 mobile:mb-5 tablet:mb-6">
              <input
                type="text"
                placeholder="Trouver une formation..."
                className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 mobile:top-2 tablet:top-2" />
            </div>

            {/* Onglets */}
            <div className="flex border-b border-gray-200 mb-8 mobile:mb-6 tablet:mb-6 mobile:overflow-x-auto tablet:overflow-x-auto">
              <button
                className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
                  activeTab === "dashboard"
                    ? "border border-orange-500 text-orange-500 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("dashboard")}
              >
                TABLEAU DE BORD
              </button>
              <button
                className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
                  activeTab === "formation"
                    ? "border border-orange-500 text-orange-500 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("formation")}
              >
                FORMATION
              </button>
              <button
                className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
                  activeTab === "favoris"
                    ? "border border-orange-500 text-orange-500 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("favoris")}
              >
                FAVORIS
              </button>
            </div>

            {/* Contenu des onglets */}
            {activeTab === "dashboard" && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 mobile:text-xl mobile:mb-4 tablet:text-xl tablet:mb-5 desktop:text-2xl">
                  Modules en cours et terminés
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile:gap-4 tablet:gap-5 desktop:gap-8">
                  {modules.map((module) => {
                    const formationCorrespondante =
                      formations.find((f) => f.titre === module.titre) ||
                      formations[0];

                    return (
                      <div
                        key={module.id}
                        className="bg-white rounded-md shadow-sm overflow-hidden mobile:shadow tablet:shadow"
                      >
                        <div
                          className={`h-56 ${formationCorrespondante?.bgClass || "bg-gradient-to-r from-pink-100 to-purple-100"} relative mobile:h-48 tablet:h-52`}
                        >
                          <img
                            src={formationCorrespondante?.image || "/images/valeur.png"}
                            alt={module.titre}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/images/valeur.png";
                            }}
                          />
                          <div className="absolute top-2 right-2">
                            <div
                              className={`${
                                module.statut === "terminé"
                                  ? "bg-green-500"
                                  : "bg-orange-500"
                              } text-white rounded-full py-1 px-3 text-xs font-medium`}
                            >
                              {module.statut === "terminé"
                                ? "Terminé"
                                : module.statut === "en cours"
                                ? "En cours"
                                : "Non commencé"}
                            </div>
                          </div>
                        </div>
                        <div className="p-4 mobile:p-3 tablet:p-3">
                          <h3 className="font-bold text-gray-800 mb-2 mobile:text-sm tablet:text-base">
                            {module.titre}
                          </h3>
                          <div className="bg-white p-2 rounded-full flex items-center space-x-3 mb-4 mobile:mb-2 tablet:mb-3 border border-orange-500">
                            <div className="relative">
                              <img
                                src={formationCorrespondante?.formateur?.image || "/images/avatar-placeholder.png"}
                                alt={formationCorrespondante?.formateur?.nom || "Formateur"}
                                className="w-14 h-14 rounded-full object-cover mobile:w-12 mobile:h-12 tablet:w-13 tablet:h-13"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = "/images/avatar-placeholder.png";
                                }}
                              />
                            </div>
                            <div>
                              <span className="block text-gray-800 font-medium text-base mobile:text-sm tablet:text-sm">
                                {formationCorrespondante?.formateur?.nom || "Formateur"}
                              </span>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Progression</span>
                              <span className="font-medium">
                                {module.pourcentage}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`${
                                  module.statut === "terminé"
                                    ? "bg-green-500"
                                    : "bg-orange-500"
                                } h-2 rounded-full`}
                                style={{ width: `${module.pourcentage}%` }}
                              ></div>
                            </div>
                          </div>

                          <div className="flex justify-between text-xs text-gray-500 items-center">
                            <div>
                              <span className="block">
                                Début: {module.dateDebut}
                              </span>
                              <span className="block">
                                Dernier accès: {module.dateDernierAcces}
                              </span>
                            </div>
                            <Link
                              to={`/etudiant/cinepro`}
                              className="bg-green-600 text-white px-3 py-1 rounded-md text-xs flex items-center hover:bg-green-700 transition-colors"
                            >
                              Continuer <ChevronRight className="h-3 w-3 ml-1" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {modules.length === 0 && (
                    <div className="col-span-full text-center py-10 bg-white rounded-md shadow-sm">
                      <p className="text-gray-500">
                        Aucun module en cours ou terminé
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "formation" && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 mobile:text-xl mobile:mb-4 tablet:text-xl tablet:mb-5 desktop:text-2xl">
                  Formation
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile:gap-4 tablet:gap-5 desktop:gap-8">
                  {(searchQuery
                    ? formations.filter(
                        (formation) =>
                          formation.titre
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          formation.formateur?.nom
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                    : formations
                  ).map((formation) => (
                    <div
                      key={formation.id}
                      className="bg-white rounded-md shadow-sm overflow-hidden mobile:shadow tablet:shadow"
                    >
                      <div
                        className={`h-56 ${formation.bgClass || "bg-gradient-to-r from-pink-100 to-purple-100"} relative mobile:h-48 tablet:h-52`}
                      >
                        <img
                          src={formation.image || "/images/valeur.png"}
                          alt={formation.titre}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/images/valeur.png";
                          }}
                        />
                      </div>
                      <div className="p-4 mobile:p-3 tablet:p-3">
                        <h3 className="font-bold text-gray-800 mb-2 mobile:text-sm tablet:text-base">
                          {formation.titre}
                        </h3>
                        <div className="bg-white p-2 rounded-full flex items-center space-x-3 mb-4 mobile:mb-2 tablet:mb-3 border border-orange-500">
                          <div className="relative">
                            <img
                              src={formation.formateur?.image || "/images/avatar-placeholder.png"}
                              alt={formation.formateur?.nom || "Formateur"}
                              className="w-14 h-14 rounded-full object-cover mobile:w-12 mobile:h-12 tablet:w-13 tablet:h-13"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "/images/avatar-placeholder.png";
                              }}
                            />
                          </div>
                          <div>
                            <span className="block text-gray-800 font-medium text-base mobile:text-sm tablet:text-sm">
                              {formation.formateur?.nom || "Formateur"}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm mb-4 mobile:text-xs mobile:mb-2 tablet:text-xs tablet:mb-3">
                          {formation.description}
                        </p>
                        <div className="flex justify-end">
                          <Link
                            to={`/etudiant/cinepro`}
                            className="bg-green-600 text-white px-3 py-1 rounded-md text-xs flex items-center hover:bg-green-700 transition-colors"
                          >
                            Continuer <ChevronRight className="h-3 w-3 ml-1" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(searchQuery
                    ? formations.filter(
                        (formation) =>
                          formation.titre
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          formation.formateur?.nom
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                    : formations
                  ).length === 0 && (
                    <div className="col-span-full text-center py-10 bg-white rounded-md shadow-sm">
                      <p className="text-gray-500">
                        {searchQuery
                          ? `Aucune formation trouvée pour "${searchQuery}"`
                          : "Aucune formation disponible"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "favoris" && (
              <div className="bg-white rounded-md shadow-sm p-6 text-center">
                <p className="text-gray-500">Aucun favori pour le moment</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EtudiantDashboard;
